import { registerAuditLabels, type StatusMap } from "@vixio/ui";

/** Tipos das linhas e RPCs do módulo content (ver modules/content/README.md). */

export type EntryKind = "page" | "post";
export type VersionState = "draft" | "in_review" | "rejected" | "scheduled" | "published" | "superseded";
export type Channel = "instagram" | "linkedin" | "blog" | "newsletter" | "whatsapp" | "youtube";
export type IdeaStatus = "backlog" | "scripting" | "checked" | "in_production" | "published" | "dropped";

export const ENTRY_STATUS: StatusMap = {
  draft: { label: "nunca publicado", tone: "neutral" },
  published: { label: "publicado", tone: "ok" },
  archived: { label: "arquivado", tone: "warn" },
};

export const VERSION_STATE: StatusMap = {
  draft: { label: "rascunho", tone: "neutral" },
  in_review: { label: "em revisão", tone: "info" },
  rejected: { label: "devolvida", tone: "warn" },
  scheduled: { label: "agendada", tone: "accent" },
  published: { label: "publicada", tone: "ok" },
  superseded: { label: "substituída", tone: "neutral" },
};

export const IDEA_STATUS: StatusMap = {
  backlog: { label: "na fila", tone: "neutral" },
  scripting: { label: "roteirizando", tone: "info" },
  checked: { label: "roteiro aprovado", tone: "accent" },
  in_production: { label: "em produção", tone: "info" },
  published: { label: "publicada", tone: "ok" },
  dropped: { label: "descartada", tone: "warn" },
};

export const EVIDENCE_STATUS: StatusMap = {
  verified: { label: "verificada", tone: "ok" },
  pending: { label: "pendente", tone: "warn" },
  retired: { label: "retirada", tone: "crit" },
};

export const CHANNEL_LABEL: Record<Channel, string> = {
  instagram: "Instagram",
  linkedin: "LinkedIn",
  blog: "Blog",
  newsletter: "Newsletter",
  whatsapp: "WhatsApp",
  youtube: "YouTube",
};

export const FORMAT_OPTIONS = [
  { value: "reel", label: "Reel" },
  { value: "post", label: "Post" },
  { value: "carrossel", label: "Carrossel" },
  { value: "stories", label: "Stories" },
  { value: "artigo", label: "Artigo" },
  { value: "status", label: "Status" },
  { value: "boletim", label: "Boletim" },
  { value: "video", label: "Vídeo" },
];

export const WEEKDAYS = ["", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado", "Domingo"];

export const METRIC_WINDOWS = [
  { value: "24h", label: "24 horas" },
  { value: "72h", label: "72 horas" },
  { value: "7d", label: "7 dias" },
  { value: "30d", label: "30 dias" },
] as const;

export const SCRIPT_BLOCKS = [
  { key: "hook", label: "Gancho", time: "0-3 s", hint: "Frase que quebra a rolagem, com o número na tela. Sem saudação." },
  { key: "proof", label: "Prova", time: "3-15 s", hint: "Mostre a manchete ou o gráfico e leia a fonte com o ano." },
  { key: "critique", label: "Crítica", time: "15-30 s", hint: "O erro comum do mercado: comportamento, nunca pessoa." },
  { key: "cost", label: "Custo", time: "30-45 s", hint: "Em dinheiro, tempo ou paciente perdido. Estimativa própria: diga que é estimativa." },
  { key: "turn", label: "Virada", time: "45-60 s", hint: "A sua visão." },
  { key: "cta", label: "Chamada", time: "60-75 s", hint: "Uma ação simples. No LinkedIn, uma pergunta que peça um caso ou um número." },
] as const;
export type ScriptBlockKey = (typeof SCRIPT_BLOCKS)[number]["key"];

export interface Block {
  type: string;
  key?: string;
  data?: unknown;
  [k: string]: unknown;
}

export interface CheckIssue {
  kind: string;
  severity: "block" | "warn";
  message: string;
  match?: string | null;
  source_ref?: string | null;
}
export interface CheckReport {
  ok: boolean;
  blocking: number;
  warnings: number;
  channel?: string | null;
  words?: number;
  issues: CheckIssue[];
  check_id?: string;
}

export interface EntryView {
  id: string;
  org_id: string;
  kind: EntryKind;
  post_type: "article" | "newsletter" | "note" | null;
  slug: string;
  locale: string;
  status: "draft" | "published" | "archived";
  origin: string;
  idea_id: string | null;
  path: string;
  category_id: string | null;
  category_name: string | null;
  author_person_id: string | null;
  published_version_id: string | null;
  published_version: number | null;
  published_title: string | null;
  first_published_at: string | null;
  last_published_at: string | null;
  archived_at: string | null;
  archived_reason: string | null;
  latest_version_id: string;
  latest_version: number;
  latest_state: VersionState;
  latest_title: string;
  scheduled_for: string | null;
  latest_updated_at: string;
  latest_blocking: number;
  latest_warnings: number;
  tags: string[];
  created_at: string;
  updated_at: string;
}

export interface EntryVersion {
  id: string;
  entry_id: string;
  version: number;
  title: string;
  excerpt: string | null;
  body_md: string | null;
  blocks: Block[];
  seo_title: string | null;
  seo_description: string | null;
  cover_media_id: string | null;
  og_media_id: string | null;
  canonical_url: string | null;
  noindex: boolean;
  state: VersionState;
  change_note: string | null;
  check_report: CheckReport | null;
  submitted_at: string | null;
  reviewed_at: string | null;
  review_note: string | null;
  publish_at: string | null;
  published_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface Category { id: string; slug: string; name: string; description: string | null }
export interface Pillar { id: string; code: string; name: string; function_text: string | null; weight_pct: number; active: boolean }
export interface PillarMix { pillar_id: string; code: string; name: string; weight_pct: number; active: boolean; publications_30d: number; actual_pct: number }
export interface WeekSlot { id: string; weekday: number; channel: Channel; format: string; pillar_id: string | null; note: string | null; minimum: boolean }

export interface Evidence {
  id: string;
  org_id: string;
  code: string;
  source: string;
  year: number | null;
  sample: string | null;
  fact: string | null;
  inference: string | null;
  risk: string | null;
  limitation: string | null;
  status: "verified" | "pending" | "retired";
  url: string | null;
  vendor_source: boolean;
  region: string | null;
  notes: string | null;
  ideas_count: number;
  updated_at: string;
}

export interface IdeaView {
  id: string;
  org_id: string;
  number: number;
  title: string;
  turn: string | null;
  channel: Channel;
  format: string;
  status: IdeaStatus;
  planned_for: string | null;
  notes: string | null;
  source_ref: string | null;
  pillar_id: string | null;
  pillar_code: string | null;
  pillar_name: string | null;
  evidence_codes: string[];
  has_unverified_evidence: boolean;
  script_id: string | null;
  script_version: number | null;
  script_state: "draft" | "approved" | "superseded" | null;
  last_check_ok: boolean | null;
  last_check_blocking: number | null;
  last_check_warnings: number | null;
  last_check_at: string | null;
  entry_id: string | null;
  publications: number;
  updated_at: string;
}

export interface Script {
  id: string;
  idea_id: string;
  version: number;
  hook: string | null;
  proof: string | null;
  critique: string | null;
  cost: string | null;
  turn: string | null;
  cta: string | null;
  source_card: string | null;
  state: "draft" | "approved" | "superseded";
  last_check_id: string | null;
  approved_at: string | null;
}

export interface CheckRun { id: string; target_id: string; ok: boolean; blocking: number; warnings: number; report: CheckReport; created_at: string; content_hash: string }

export interface CalendarItem {
  org_id: string;
  day: string;
  kind: "pauta" | "agendado" | "publicado";
  channel: string;
  format: string;
  title: string;
  pillar_code: string | null;
  ref_id: string;
  status: string;
}

export interface PublicationMetrics {
  publication_id: string;
  org_id: string;
  channel: Channel;
  format: string;
  published_at: string;
  url: string | null;
  idea_id: string | null;
  entry_id: string | null;
  idea_title: string | null;
  idea_number: number | null;
  pillar_code: string | null;
  window: string | null;
  views: number | null;
  reach: number | null;
  saves: number | null;
  shares: number | null;
  comments: number | null;
  decision_maker_comments: number | null;
  profile_visits: number | null;
  qualified_conversations: number | null;
  retention_3s_pct: number | null;
  retention_15s_pct: number | null;
  recorded_at: string | null;
}

export interface EditorialHealth {
  org_id: string;
  publications_30d: number;
  qualified_30d: number;
  ideas_open: number;
  evidences_verified: number;
  evidences_pending: number;
}

export interface CheckerRule {
  id: string;
  kind: "forbidden" | "needs_evidence" | "max_count" | "required";
  channel: string | null;
  pattern: string;
  case_sensitive: boolean;
  max_count: number | null;
  severity: "block" | "warn";
  message: string;
  source_ref: string | null;
  active: boolean;
}

export interface Redirect { id: string; from_path: string; to_path: string; status_code: number; active: boolean; note: string | null; updated_at: string }
export interface Faq { id: string; group_key: string; question: string; answer_md: string; position: number; published: boolean }
export interface Testimonial {
  id: string;
  author_name: string | null;
  author_role: string;
  organization_label: string | null;
  text: string;
  consent_evidence: string | null;
  status: "pending" | "approved" | "withdrawn";
  position: number;
  approved_at: string | null;
}
export interface Media { id: string; path: string; bucket: string; mime_type: string; size_bytes: number; width: number | null; height: number | null; alt_text: string | null; caption: string | null; credit: string | null; created_at: string }
export interface SiteSetting { id: string; key: string; value: unknown; category: string; is_public: boolean; updated_at: string }
export interface Menu { id: string; key: string; title: string }
export interface MenuItem { id: string; menu_id: string; parent_id: string | null; position: number; label: string; href: string }

export interface Preview {
  preview: true;
  expires_at: string;
  kind: EntryKind;
  slug: string;
  path: string;
  version: number;
  state: VersionState;
  title: string;
  excerpt: string | null;
  body_md: string | null;
  blocks: Block[];
  seo_title: string | null;
  seo_description: string | null;
}

export const TESTIMONIAL_STATUS: StatusMap = {
  pending: { label: "aguardando aprovação", tone: "warn" },
  approved: { label: "aprovado", tone: "ok" },
  withdrawn: { label: "retirado", tone: "neutral" },
};

export const RULE_KIND_LABEL: Record<CheckerRule["kind"], string> = {
  forbidden: "frase proibida",
  needs_evidence: "exige evidência",
  max_count: "limite de ocorrências",
  required: "obrigatório",
};

declare module "@vixio/data" {
  interface RpcRegistry {
    "content.create_entry": { args: { p_org: string; p_kind: EntryKind; p_slug: string; p_title: string; p_post_type?: string | null }; returns: string };
    "content.update_entry": { args: { p_entry: string; p_slug?: string | null; p_category?: string | null; p_author_person?: string | null; p_post_type?: string | null }; returns: null };
    "content.save_draft": {
      args: {
        p_entry: string;
        p_title: string;
        p_excerpt?: string | null;
        p_body_md?: string | null;
        p_blocks?: Block[];
        p_seo_title?: string | null;
        p_seo_description?: string | null;
        p_cover_media?: string | null;
        p_og_media?: string | null;
        p_canonical_url?: string | null;
        p_noindex?: boolean;
        p_change_note?: string | null;
      };
      returns: string;
    };
    "content.submit_version": { args: { p_version: string }; returns: CheckReport };
    "content.reject_version": { args: { p_version: string; p_note: string }; returns: null };
    "content.publish_version": { args: { p_version: string; p_publish_at?: string | null }; returns: "published" | "scheduled" };
    "content.cancel_schedule": { args: { p_version: string }; returns: null };
    "content.archive_entry": { args: { p_entry: string; p_reason: string }; returns: null };
    "content.discard_draft": { args: { p_version: string }; returns: null };
    "content.delete_entry": { args: { p_entry: string }; returns: null };
    "content.create_preview": { args: { p_version: string; p_hours?: number }; returns: { id: string; token: string; expires_at: string } };
    "content.revoke_preview": { args: { p_preview: string }; returns: null };
    "content.get_preview": { args: { p_token: string }; returns: Preview };
    "content.set_entry_terms": { args: { p_entry: string; p_category: string | null; p_tags: string[] }; returns: null };
    "content.upsert_category": { args: { p_org: string; p_slug: string | null; p_name: string; p_description?: string | null; p_id?: string | null }; returns: string };
    "content.check_text": { args: { p_org: string; p_text: string; p_channel?: string | null; p_has_evidence?: boolean }; returns: CheckReport };
    "content.upsert_rule": {
      args: {
        p_org: string;
        p_kind: CheckerRule["kind"];
        p_pattern: string;
        p_message: string;
        p_severity?: "block" | "warn";
        p_channel?: string | null;
        p_case_sensitive?: boolean;
        p_max_count?: number | null;
        p_source_ref?: string | null;
        p_active?: boolean;
        p_id?: string | null;
      };
      returns: string;
    };
    "content.install_checker_defaults": { args: { p_org: string }; returns: number };
    "content.upsert_pillar": { args: { p_org: string; p_code: string; p_name: string; p_weight_pct: number; p_function_text?: string | null; p_active?: boolean; p_id?: string | null }; returns: string };
    "content.set_week_slot": { args: { p_org: string; p_weekday: number; p_channel: Channel; p_format: string; p_pillar?: string | null; p_note?: string | null; p_minimum?: boolean }; returns: string };
    "content.delete_week_slot": { args: { p_id: string }; returns: null };
    "content.upsert_evidence": {
      args: {
        p_org: string;
        p_code: string;
        p_source: string;
        p_year?: number | null;
        p_sample?: string | null;
        p_fact?: string | null;
        p_inference?: string | null;
        p_risk?: string | null;
        p_limitation?: string | null;
        p_status?: Evidence["status"];
        p_url?: string | null;
        p_vendor_source?: boolean;
        p_region?: string | null;
        p_notes?: string | null;
      };
      returns: string;
    };
    "content.upsert_idea": {
      args: {
        p_org: string;
        p_title: string;
        p_pillar?: string | null;
        p_turn?: string | null;
        p_channel?: Channel;
        p_format?: string;
        p_planned_for?: string | null;
        p_notes?: string | null;
        p_source_ref?: string | null;
        p_id?: string | null;
      };
      returns: string;
    };
    "content.set_idea_evidences": { args: { p_idea: string; p_evidences: string[] }; returns: null };
    "content.set_idea_status": { args: { p_idea: string; p_status: "backlog" | "dropped" }; returns: null };
    "content.save_script": {
      args: { p_idea: string; p_hook: string; p_proof: string; p_critique: string; p_cost: string; p_turn: string; p_cta: string; p_source_card?: string | null };
      returns: string;
    };
    "content.check_script": { args: { p_script: string }; returns: CheckReport };
    "content.approve_script": { args: { p_script: string }; returns: null };
    "content.create_post_from_idea": { args: { p_idea: string; p_slug?: string | null }; returns: string };
    "content.register_publication": {
      args: { p_org: string; p_channel: Channel; p_format: string; p_published_at?: string | null; p_url?: string | null; p_idea?: string | null; p_entry?: string | null; p_note?: string | null };
      returns: string;
    };
    "content.record_metrics": {
      args: {
        p_publication: string;
        p_window: string;
        p_views?: number | null;
        p_reach?: number | null;
        p_saves?: number | null;
        p_shares?: number | null;
        p_comments?: number | null;
        p_decision_maker_comments?: number | null;
        p_profile_visits?: number | null;
        p_qualified_conversations?: number | null;
        p_retention_3s_pct?: number | null;
        p_retention_15s_pct?: number | null;
        p_note?: string | null;
      };
      returns: string;
    };
    "content.upsert_redirect": { args: { p_org: string; p_from_path: string; p_to_path: string; p_status_code?: number; p_active?: boolean; p_note?: string | null }; returns: string };
    "content.delete_redirect": { args: { p_id: string }; returns: null };
    "content.set_menu": { args: { p_org: string; p_key: string; p_title: string; p_items: Array<{ label: string; href: string; children?: Array<{ label: string; href: string }> }> }; returns: string };
    "content.upsert_faq": { args: { p_org: string; p_question: string; p_answer_md: string; p_group_key?: string; p_position?: number; p_published?: boolean; p_id?: string | null }; returns: string };
    "content.delete_faq": { args: { p_id: string }; returns: null };
    "content.upsert_testimonial": {
      args: {
        p_org: string;
        p_author_role: string;
        p_text: string;
        p_author_name?: string | null;
        p_organization_label?: string | null;
        p_consent_evidence?: string | null;
        p_position?: number;
        p_id?: string | null;
      };
      returns: string;
    };
    "content.approve_testimonial": { args: { p_id: string }; returns: null };
    "content.withdraw_testimonial": { args: { p_id: string }; returns: null };
    "content.begin_media_upload": {
      args: { p_org: string; p_name: string; p_content_type: string; p_size: number };
      returns: { upload_id: string; bucket: string; storage_path: string; max_bytes: number };
    };
    "content.finish_media_upload": {
      args: { p_upload: string; p_alt_text?: string | null; p_caption?: string | null; p_credit?: string | null; p_width?: number | null; p_height?: number | null };
      returns: { media_id: string; path: string; site_path: string };
    };
    "content.register_media": {
      args: { p_org: string; p_path: string; p_mime_type: string; p_size_bytes: number; p_alt_text?: string | null; p_caption?: string | null; p_credit?: string | null; p_width?: number | null; p_height?: number | null };
      returns: string;
    };
    "content.update_media": { args: { p_media: string; p_alt_text: string | null; p_caption?: string | null; p_credit?: string | null }; returns: null };
    "content.delete_media": { args: { p_media: string }; returns: null };
    "content.set_setting": { args: { p_org: string; p_key: string; p_value: unknown; p_category?: string; p_is_public?: boolean }; returns: string };
    "content.delete_setting": { args: { p_org: string; p_key: string }; returns: null };
    "content.set_rebuild_hook": {
      args: { p_org: string; p_url: string; p_secret_ref?: string | null; p_check_url?: string | null; p_debounce_seconds?: number; p_enabled?: boolean };
      returns: string;
    };
    "content.rebuild_site": { args: { p_org: string }; returns: string };
  }
}

registerAuditLabels({
  "content_entry.created": "Conteúdo criado",
  "content_entry.updated": "Endereço ou dados alterados",
  "content_entry.archived": "Conteúdo arquivado",
  "content_entry.deleted": "Conteúdo apagado",
  "content_entry.terms_set": "Categoria e tags definidas",
  "content_version.saved": "Rascunho salvo",
  "content_version.submitted": "Enviado para revisão",
  "content_version.rejected": "Devolvido na revisão",
  "content_version.scheduled": "Publicação agendada",
  "content_version.schedule_canceled": "Agendamento cancelado",
  "content_version.published": "Versão publicada",
  "content_version.discarded": "Rascunho descartado",
  "content_preview.created": "Prévia gerada",
  "content_preview.revoked": "Prévia revogada",
  "content_preview.viewed": "Prévia aberta",
  "content_idea.upserted": "Pauta salva",
  "content_idea.evidences_set": "Evidências da pauta definidas",
  "content_idea.status_set": "Situação da pauta alterada",
  "content_idea.post_created": "Post criado a partir da pauta",
  "content_script.saved": "Roteiro salvo",
  "content_script.checked": "Roteiro verificado",
  "content_script.approved": "Roteiro aprovado",
  "content_evidence.upserted": "Evidência salva",
  "content_publication.registered": "Publicação registrada",
  "content_metrics.recorded": "Métricas registradas",
  "content_setting.set": "Configuração do site alterada",
  "content_site.rebuild_hook_set": "Gancho de atualização do site alterado",
  "content_site.rebuild_requested": "Atualização do site pedida",
  "content_site.rebuild_failed": "Atualização do site falhou",
});
